import { FC, useEffect, useRef, useState } from 'react';
import {
  Button,
  CheckBox,
  Heading,
  Link,
  Modal,
  Types,
  Text,
  InlineMessages,
  InlineMessagesType,
} from '@marriott/mi-ui-library';
import { MSnackbar, SNACKBAR_MODAL_TYPE } from '@marriott/mi-web-ui-kit';
import clsx from 'clsx';
import { PickupStatsAlertSubscriber } from '@marriott/mi-groups-graphql';
import { PickupStatsAlertsProps, Recipient } from './PickupStatsAlerts.types';
import { StyledPickupStatsAlerts } from './PickupStatsAlerts.styles';
import { PickupStatsBackPopOver } from './__mock__/__mock__';
import { EventBookingDetails } from '../../../rooming-list/PlannerNewEvent/EventBookingDetails';
import { EMAIL_VALIDATION_REGEX } from '../../../../../constants';
import { TextFormField } from '../../../../../molecules';
import { RESLINK_PICKUP_STATS_MAX_RECIPIENTS, EMAIL_MAX_LENGTH } from '../../../../constants';

import { useDttStore } from '../../../../../store';
import { useReslinkAssociateStore } from '../../../../store';
import { isPastDate, isToday, getFormattedDateString } from '../../../../../utils';
import { isActivePickupAlertSubscriber } from '../../../../utils/reslink';

const DEFAULT_RECIPIENT: Omit<Recipient, 'email'> = {
  frequency: { daily: false, weekly: false, monthly: false },
  milestone: { '25': false, '50': false, '75': false, '90': false },
};

const getRecipientFromSubscriber = (subscriber: PickupStatsAlertSubscriber): Recipient => {
  const thresholds = subscriber.preferences?.thresholds || [];

  return {
    email: subscriber.email.trim().toLowerCase(),
    frequency: {
      daily: !!subscriber.preferences?.daily,
      weekly: !!subscriber.preferences?.weekly,
      monthly: !!subscriber.preferences?.monthly,
    },
    milestone: {
      '25': thresholds.includes(25),
      '50': thresholds.includes(50),
      '75': thresholds.includes(75),
      '90': thresholds.includes(90),
    },
  };
};

export const PickupStatsAlerts: FC<PickupStatsAlertsProps> = ({
  propertyIndex,
  eventTrackingData,
  bookingStatsLabels,
  groupRatesLabels,
  emailNotificationLabels,
  alertSubscriptionsByKey = {},
  onCancel,
  onSave,
  pickupStatsResponseStatus,
  apiFailureText,
}) => {
  const {
    heading,
    backLabel,
    addRecipientsHeading,
    addRecipientsHelperText,
    emailLabel,
    emailPlaceholder,
    addEmailCta,
    recipientsTableHeading,
    recipientsTableHelperText,
    frequencyLabel,
    frequencyOptions,
    milestoneLabel,
    milestoneOptions,
    cancelCta,
    saveCta,
    saveCtaTrackLocation,
    saveCtaTrackDescription,
    emailInvalidErrorText,
    emailDuplicateErrorText,
    maxRecipientsErrorText,
    pastStartDateHeading,
    pastStartDateText,
    alertSuccessMessage,
    recipientValidationError,
    emailMaxLengthErrorText,
  } = emailNotificationLabels;

  const { isPickupStatsAlertsEnabled } = useDttStore();
  const { PUSAEnabled } = useReslinkAssociateStore();
  const isPickupStatsAlertsAllowed = isPickupStatsAlertsEnabled || PUSAEnabled;

  const propertyData = eventTrackingData.find(data => data.propertyIndex === propertyIndex);
  const alertIdentityKey = propertyData?.pickupAlertIdentityKey || '';
  const isStartDatePast = propertyData?.startDate
    ? isToday(getFormattedDateString(propertyData.startDate, 'dateWithDayMonthAndYear')) ||
      isPastDate(propertyData.startDate)
    : false;

  const [emailInput, setEmailInput] = useState('');
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [emailError, setEmailError] = useState('');
  const [emailEditingIndex, setEmailEditingIndex] = useState<number | null>(null);
  const [emailEditValue, setEmailEditValue] = useState('');
  const [isEmailDirty, setEmailIsDirty] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [savedRecipients, setSavedRecipients] = useState<Recipient[]>([]);
  const [toastMessage, setToastMessage] = useState<{
    modalType: SNACKBAR_MODAL_TYPE;
    message: string;
    className?: string;
  } | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const backButtonRef = useRef<HTMLAnchorElement | null>(null);
  const { heading: modalHeading, message, cancelCtaLabel, leaveCtaLabel } = PickupStatsBackPopOver;
  const [errorRowIndexes, setErrorRowIndexes] = useState<number[]>([]);

  const getEmailValidationResult = (value: string) => {
    const normalizedEmails = value
      .trim()
      .split(/[;,]/)
      .map(email => email.trim().toLowerCase())
      .filter(Boolean);

    const hasInvalidEmail = normalizedEmails.some(email => !EMAIL_VALIDATION_REGEX.test(email));

    const hasEmailLengthExceeded = normalizedEmails.some(email => email.length > EMAIL_MAX_LENGTH);

    return {
      normalizedEmails,
      hasInvalidEmail,
      hasEmailLengthExceeded,
    };
  };

  const handleEmailInputChange = (value: string) => {
    setEmailInput(value);
    if (toastMessage?.modalType === SNACKBAR_MODAL_TYPE.ERROR) {
      setToastMessage(null);
    }

    if (!/\s$/.test(value) || !value.trim()) {
      setEmailError('');
      return;
    }

    const { hasInvalidEmail, hasEmailLengthExceeded } = getEmailValidationResult(value);

    if (hasInvalidEmail) {
      setEmailError(emailInvalidErrorText);
    } else if (hasEmailLengthExceeded) {
      setEmailError(emailMaxLengthErrorText);
    } else {
      setEmailError('');
    }
  };

  const addRecipient = () => {
    const trimmedEmail = emailInput.trim();
    if (!trimmedEmail) return;

    const remainingEmailSlots = RESLINK_PICKUP_STATS_MAX_RECIPIENTS - recipients.length;
    if (remainingEmailSlots <= 0) {
      setEmailError(maxRecipientsErrorText);
      return;
    }

    const { normalizedEmails, hasInvalidEmail, hasEmailLengthExceeded } = getEmailValidationResult(trimmedEmail);

    if (hasInvalidEmail) {
      setEmailError(emailInvalidErrorText);
      return;
    }

    if (hasEmailLengthExceeded) {
      setEmailError(emailMaxLengthErrorText);
      return;
    }

    const uniqueNewEmails = [...new Set(normalizedEmails)];
    const existingEmails = new Set(recipients.map(r => r.email.trim()));

    const hasDuplicateInBatch = uniqueNewEmails.length !== normalizedEmails.length;
    const hasDuplicateAgainstExisting = uniqueNewEmails.some(email => existingEmails.has(email));

    if (hasDuplicateInBatch || hasDuplicateAgainstExisting) {
      setToastMessage({
        modalType: SNACKBAR_MODAL_TYPE.ERROR,
        message: emailDuplicateErrorText,
      });
      return;
    }

    if (!uniqueNewEmails.length) {
      return;
    }

    if (uniqueNewEmails.length > remainingEmailSlots) {
      setEmailError(maxRecipientsErrorText);
      return;
    }

    setRecipients(prev => [...prev, ...uniqueNewEmails.map(email => ({ email, ...DEFAULT_RECIPIENT }))]);
    setEmailInput('');
    setEmailError('');
  };

  const updateErrorRows = (index: number, isRemovingRecipient = false) => {
    const updatedErrorIndexes = isRemovingRecipient
      ? errorRowIndexes.filter(i => i !== index).map(i => (i > index ? i - 1 : i))
      : errorRowIndexes.filter(i => i !== index);

    setErrorRowIndexes(updatedErrorIndexes);
    if (updatedErrorIndexes.length === 0 && toastMessage?.message === recipientValidationError) {
      setToastMessage(null);
    }
  };

  const toggleFrequency = (index: number, key: keyof Recipient['frequency']) => {
    updateErrorRows(index);

    setRecipients(prev =>
      prev.map((r, i) => (i === index ? { ...r, frequency: { ...r.frequency, [key]: !r.frequency[key] } } : r))
    );
  };

  const toggleMilestone = (index: number, key: keyof Recipient['milestone']) => {
    updateErrorRows(index);

    setRecipients(prev =>
      prev.map((r, i) => (i === index ? { ...r, milestone: { ...r.milestone, [key]: !r.milestone[key] } } : r))
    );
  };

  const handleSave = async () => {
    const invalidIndexes = recipients.reduce<number[]>((indexes, recipient, index) => {
      const hasFrequencyOrMilestoneSelected =
        Object.values(recipient.frequency).some(Boolean) || Object.values(recipient.milestone).some(Boolean);

      if (!hasFrequencyOrMilestoneSelected) {
        indexes.push(index);
      }

      return indexes;
    }, []);

    if (invalidIndexes.length > 0) {
      setErrorRowIndexes(invalidIndexes);
      setToastMessage({
        modalType: SNACKBAR_MODAL_TYPE.ERROR,
        message: recipientValidationError,
        className: 'wide-snackbar',
      });

      document.getElementById(`freq-${invalidIndexes[0]}-daily`)?.focus();

      return;
    }

    setErrorRowIndexes([]);

    const saveResult = await onSave({ recipients });

    if (!saveResult?.success) {
      setToastMessage({
        modalType: SNACKBAR_MODAL_TYPE.ERROR,
        message: apiFailureText,
        className: 'wide-snackbar',
      });
      return;
    }

    setSavedRecipients(recipients);
    setToastMessage({
      modalType: SNACKBAR_MODAL_TYPE.SUCCESS,
      message: alertSuccessMessage,
    });
  };

  const handleCancel = () => {
    setRecipients(savedRecipients);
  };

  const handleEdit = (index: number, email: string) => {
    setEmailEditingIndex(index);
    setEmailEditValue(email);
  };

  const handleSaveEdit = (index: number) => {
    const normalizedEditedEmail = emailEditValue.trim().toLowerCase();
    if (!normalizedEditedEmail) return;

    if (!EMAIL_VALIDATION_REGEX.test(normalizedEditedEmail)) {
      setToastMessage({
        modalType: SNACKBAR_MODAL_TYPE.ERROR,
        message: emailInvalidErrorText,
      });
      setEmailEditingIndex(null);
      return;
    }

    if (normalizedEditedEmail.length > EMAIL_MAX_LENGTH) {
      setToastMessage({
        modalType: SNACKBAR_MODAL_TYPE.ERROR,
        message: emailMaxLengthErrorText,
      });
      setEmailEditingIndex(null);
      return;
    }

    const existingEmails = new Set(
      recipients
        .filter((_, recipientIndex) => recipientIndex !== index)
        .map(recipient => recipient.email.trim().toLowerCase())
    );

    if (existingEmails.has(normalizedEditedEmail)) {
      setToastMessage({
        modalType: SNACKBAR_MODAL_TYPE.ERROR,
        message: emailDuplicateErrorText,
      });
      setEmailEditingIndex(null);
      return;
    }

    if (normalizedEditedEmail !== recipients[index].email) {
      updateErrorRows(index);
    }

    setRecipients(prev => prev.map((r, i) => (i === index ? { ...r, email: normalizedEditedEmail } : r)));

    setEmailEditingIndex(null);
    setEmailError('');
  };

  const handleRemove = (index: number) => {
    updateErrorRows(index, true);

    setRecipients(prev => prev.filter((_, i) => i !== index));
  };

  const handleModalClose = () => {
    setIsModalOpen(false);

    requestAnimationFrame(() => {
      backButtonRef.current?.focus();
    });
  };

  const handleLeaveCta = () => {
    setRecipients([...savedRecipients]);
    setEmailInput('');
    setEmailEditingIndex(null);
    setEmailEditValue('');
    setIsModalOpen(false);
    onCancel();
  };

  useEffect(() => {
    setEmailIsDirty(JSON.stringify(recipients) !== JSON.stringify(savedRecipients));
  }, [recipients, savedRecipients]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (emailEditingIndex !== null && inputRef.current && !inputRef.current.contains(e.target as Node)) {
        handleSaveEdit(emailEditingIndex);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [emailEditingIndex, emailEditValue]);

  useEffect(() => {
    if (!isPickupStatsAlertsAllowed) {
      return;
    }

    const subscription = alertIdentityKey ? alertSubscriptionsByKey[alertIdentityKey] : undefined;
    const recipientsMap = new Map<string, Recipient>();

    subscription?.subscribers?.filter(isActivePickupAlertSubscriber).forEach(subscriber => {
      const normalizedEmail = subscriber.email?.trim().toLowerCase();
      if (!normalizedEmail) {
        return;
      }

      const recipient = getRecipientFromSubscriber(subscriber);
      const existingRecipient = recipientsMap.get(normalizedEmail);

      if (!existingRecipient) {
        recipientsMap.set(normalizedEmail, recipient);
        return;
      }

      recipientsMap.set(normalizedEmail, {
        email: normalizedEmail,
        frequency: {
          daily: existingRecipient.frequency.daily || recipient.frequency.daily,
          weekly: existingRecipient.frequency.weekly || recipient.frequency.weekly,
          monthly: existingRecipient.frequency.monthly || recipient.frequency.monthly,
        },
        milestone: {
          '25': existingRecipient.milestone['25'] || recipient.milestone['25'],
          '50': existingRecipient.milestone['50'] || recipient.milestone['50'],
          '75': existingRecipient.milestone['75'] || recipient.milestone['75'],
          '90': existingRecipient.milestone['90'] || recipient.milestone['90'],
        },
      });
    });

    const initialRecipients = Array.from(recipientsMap.values());

    setRecipients(initialRecipients);
    setSavedRecipients(initialRecipients);
  }, [alertSubscriptionsByKey, alertIdentityKey, isPickupStatsAlertsAllowed]);

  if (!propertyData) {
    return null;
  }

  return (
    <StyledPickupStatsAlerts data-testid="groups-PickupStatsAlerts" data-component-name="o-groups-PickupStatsAlerts">
      <Link
        ref={backButtonRef}
        text={backLabel}
        ariaLabel={backLabel}
        linkClassName="m-link-back-tertiary-button mt-4 back-cta"
        linkHref=""
        target="_self"
        callback={(event: React.FormEvent<HTMLInputElement>) => {
          event.preventDefault();
          setIsModalOpen(true);
        }}
      />
      <Heading
        variation={Types.headingType.title}
        element={Types.tags.h2}
        titleText={propertyData.propertyName}
        fontSize={Types.size.medium}
        customClass="mt-5 property-name"
      />

      <EventBookingDetails
        bookingStatsLabels={bookingStatsLabels}
        bookingStatsData={propertyData.bookingStatsData}
        groupRatesLabels={groupRatesLabels}
        groupRatesData={propertyData.groupRatesData}
        showGroupRates={false}
        isReslink
      />

      {isPickupStatsAlertsAllowed && (
        <>
          <Heading
            variation={Types.headingType.title}
            element={Types.tags.h3}
            titleText={heading}
            fontSize={Types.size.small}
            customClass="mb-4"
          />

          <div className="email-notification-section">
            {!pickupStatsResponseStatus.hasData && pickupStatsResponseStatus.hasError ? (
              <InlineMessages type={InlineMessagesType.Error} title={apiFailureText} />
            ) : (
              <>
                <div className="add-recipients">
                  <h4 className="add-recipients-heading">
                    {!isStartDatePast ? addRecipientsHeading : pastStartDateHeading}
                  </h4>
                  <p className="add-recipients-helper">
                    {!isStartDatePast ? addRecipientsHelperText : pastStartDateText}
                  </p>

                  {!isStartDatePast && (
                    <div className="add-email-row">
                      <div className="email-input-wrapper">
                        <TextFormField
                          label={emailLabel}
                          placeholder={emailPlaceholder}
                          className="m-input-text-field"
                          value={emailInput}
                          showErrorMessage={!!emailError}
                          errorMessage={emailError}
                          onChange={e => handleEmailInputChange(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && addRecipient()}
                        />
                      </div>
                      <Button
                        buttonCopy={addEmailCta}
                        className={clsx('m-button-s', 'm-button-secondary', 'add-email-btn', !emailInput && 'disabled')}
                        callback={addRecipient}
                        isDisabled={!emailInput}
                      />
                    </div>
                  )}
                </div>

                <div className="section-divider" />

                {recipients.length > 0 && !isStartDatePast && (
                  <div className="recipients-table-section">
                    <div className="recipients-table">
                      <div className="recipients-table-heading-row">
                        <div className="col-recipient">{recipientsTableHeading}</div>
                        <div className="col-frequency">{frequencyLabel}</div>
                        <div className="col-milestone">{milestoneLabel}</div>
                      </div>
                      <div className="recipients-table-subheader">
                        <div className="col-recipient">{recipientsTableHelperText}</div>
                        <div className="col-frequency">
                          {frequencyOptions.map(opt => (
                            <span key={opt.value}>{opt.label}</span>
                          ))}
                        </div>
                        <div className="col-milestone">
                          {milestoneOptions.map(opt => (
                            <span key={opt.value}>{opt.label}</span>
                          ))}
                        </div>
                      </div>
                      {recipients.map((recipient, index) => (
                        <div
                          className={clsx(
                            'recipients-table-row',
                            errorRowIndexes?.includes(index) && 'recipients-table-row-error'
                          )}
                          key={index}
                        >
                          <div className="col-recipient  recipients-details">
                            {emailEditingIndex === index ? (
                              <div className="edit-mode ">
                                <input
                                  ref={inputRef}
                                  value={emailEditValue}
                                  onChange={e => setEmailEditValue(e.target.value)}
                                  className="email-edit-input"
                                  data-testid="email-edit-input"
                                  onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                      handleSaveEdit(index);
                                    }
                                    if (e.key === 'Escape') {
                                      setEmailEditingIndex(null);
                                    }
                                  }}
                                />
                                <Button
                                  ariaLabel={`${emailEditValue} Save`}
                                  className="tick-link"
                                  data-testid="tick-link"
                                  callback={() => handleSaveEdit(index)}
                                >
                                  <span className="icon-check" />
                                </Button>
                              </div>
                            ) : (
                              <>
                                <span>{recipient.email}</span>

                                <div className="actions">
                                  <Button
                                    ariaLabel={`${recipient.email} Edit`}
                                    className="edit-link mr-4"
                                    callback={() => handleEdit(index, recipient.email)}
                                    data-testid="edit-link"
                                  >
                                    <span className="icon-edit" />
                                  </Button>

                                  <Button
                                    ariaLabel={`${recipient.email} Remove`}
                                    className="cancel-link"
                                    callback={() => handleRemove(index)}
                                    data-testid="cancel-link"
                                  >
                                    <span className="icon-clear" />
                                  </Button>
                                </div>
                              </>
                            )}
                          </div>
                          <div className="col-frequency">
                            <div className="checkbox-group">
                              {frequencyOptions.map(opt => (
                                <div key={opt.value} className="checkbox-item">
                                  <CheckBox
                                    ariaLabel={`${recipient.email} ${opt.value} ${frequencyLabel}`}
                                    checkboxId={`freq-${index}-${opt.value}`}
                                    checkboxName={`freq-${index}-${opt.value}`}
                                    checked={recipient.frequency[opt.value as keyof Recipient['frequency']]}
                                    onChange={() => toggleFrequency(index, opt.value as keyof Recipient['frequency'])}
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="col-milestone">
                            <div className="checkbox-group">
                              {milestoneOptions.map(opt => (
                                <div key={opt.value} className="checkbox-item">
                                  <CheckBox
                                    ariaLabel={`${recipient.email} ${opt.value}% ${milestoneLabel}`}
                                    checkboxId={`milestone-${index}-${opt.value}`}
                                    checkboxName={`milestone-${index}-${opt.value}`}
                                    checked={recipient.milestone[opt.value as keyof Recipient['milestone']]}
                                    onChange={() => toggleMilestone(index, opt.value as keyof Recipient['milestone'])}
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="section-divider" />
                  </div>
                )}
                {!isStartDatePast && (
                  <div className="alerts-footer">
                    <Button
                      buttonCopy={cancelCta}
                      className="m-button-secondary"
                      callback={handleCancel}
                      isDisabled={!isEmailDirty}
                    />
                    <Button
                      buttonCopy={saveCta}
                      className="m-button-primary custom_click_track"
                      callback={handleSave}
                      isDisabled={!isEmailDirty}
                      custom_click_track_value={`${saveCtaTrackLocation}|${saveCtaTrackDescription}|internal`}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

      {isModalOpen && (
        <Modal
          handleBlur={true}
          role="dialog"
          show={isModalOpen}
          popupOpenState={isModalOpen}
          setPopupOpenState={handleModalClose}
          onClose={handleModalClose}
          labelledBy="pickupStatsAlertsModal"
          modalId="pickupStatsAlertsModal"
        >
          <Modal.Header labelText={modalHeading} popupHeaderOnCLoseFunc={handleModalClose}></Modal.Header>
          <Modal.Body>
            <Text
              copyText={message}
              customClass="mb-3 d-inline-block margin-top"
              fontSize={Types.size.medium}
              element={Types.tags.paragraph}
            />
            <div className="mb-5 d-flex justify-content-end">
              <Button
                buttonCopy={cancelCtaLabel}
                className="m-button-m m-button-secondary mt-1 mx-4"
                callback={handleModalClose}
              />
              <Button
                buttonCopy={leaveCtaLabel}
                className="m-button-m m-button-primary mt-1 "
                callback={handleLeaveCta}
              />
            </div>
          </Modal.Body>
        </Modal>
      )}

      {toastMessage && (
        <MSnackbar
          modalType={toastMessage.modalType}
          errorMessage={toastMessage.modalType === SNACKBAR_MODAL_TYPE.ERROR ? toastMessage.message : undefined}
          successMessage={toastMessage.modalType === SNACKBAR_MODAL_TYPE.SUCCESS ? toastMessage.message : undefined}
          aria-label={toastMessage.message}
          autoHideDuration={10000}
          onClose={() => setToastMessage(null)}
          className={toastMessage.className}
        />
      )}
    </StyledPickupStatsAlerts>
  );
};
